import React, { createContext, ReactNode, useEffect, useState } from 'react';
import api from '../services/api';
import { JwtService } from '../services/jwtService';
import { Role } from '../domain/Role';
import { getUserDisplayName } from '../utils/UserDisplayName';

interface User {
  name: string;
  email: string;
  access_token?: string;
  roles: Role[];
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  useEffect(() => {
    const token = JwtService.getJwt();
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser({
        name: getUserDisplayName(payload),
        email: payload.email,
        access_token: token,
        roles: payload.realm_access.roles || []
      });
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    const token = JwtService.getJwt();
    if (token) {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      const now = Date.now() / 1000;

      if (tokenData.exp < now) {
        refreshToken();
      } else {
        setTimeout(() => {
          refreshToken();
        }, (tokenData.exp - now) * 1000);
      }
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    JwtService.clear();

    const response = await api.post('/login', {
      username: email,
      password: password,
    });

    localStorage.setItem('refresh_token', response.data.refresh_token);
    JwtService.saveJwt(response.data.access_token);

    const payload = JSON.parse(atob(response.data.access_token.split('.')[1]));
    setUser({
      name: getUserDisplayName(payload),
      email: payload.email,
      access_token: response.data.access_token,
      roles: payload.realm_access.roles || []
    });
    setIsAuthenticated(true);
  };

  const logout = () => {
    JwtService.clear();
    setUser(null);
    setIsAuthenticated(false);
  };

  // Función para renovar el access token
  const refreshToken = async () => {
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      try {
        const response = await api.post('/refresh', { refresh_token: refresh_token });
        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Actualizar tokens
        JwtService.saveJwt(access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        // Actualizar el usuario
        const userInfo = JSON.parse(atob(access_token.split('.')[1]));
        setUser({
          name: getUserDisplayName(userInfo),
          email: userInfo.email,
          access_token: response.data.access_token,
          roles: userInfo.realm_access.roles || []
        });
        setIsAuthenticated(true);
      } catch (error) {
        logout();
        window.location.href = '/login';
      }
    } else {
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
